import { Avatar } from "antd";

interface Props {
	name: string;
	src: string | null;
	size?: number;
	style?: React.CSSProperties;
}

export function ColorAvatar(props: Props) {
	const { name, src, size, style: customStyle } = props;

	if (!name) return null;

	const hue = hashCode(name) % 360;
	// The photo-less avatar keeps the half-strength wash it has always had, but
	// carries it as an ALPHA on the background instead of `opacity` on the
	// element: `opacity` fades the initials with it, and these initials are the
	// only thing in the circle. The dark tone of the same hue is what makes a
	// letter legible on that wash (EQU-1217).
	const style: React.CSSProperties = src
		? { ...customStyle, backgroundColor: `hsl(${hue}, 50%, 60%)` }
		: {
				...customStyle,
				backgroundColor: `hsla(${hue}, 50%, 60%, 0.5)`,
				color: `hsl(${hue}, 50%, 25%)`,
			};

	const avatarName = getNameInitials(name);

	return (
		// 🪤 `src` is handed over ONLY when there is an image. AntD renders `src`
		// INSTEAD of the children whenever it is a React element — including one
		// that returns null — so a constant `<Img />` left every photo-less
		// avatar an empty coloured circle and the initials below never painted.
		<Avatar
			size={size || 52}
			src={src ? <Img src={src} /> : undefined}
			style={style}
		>
			{avatarName}
		</Avatar>
	);
}

const Img = ({ src }: { src: string }) => (
	<img referrerPolicy="no-referrer" src={src} alt="avatar" />
);

function getNameInitials(name: string) {
	return name
		.replaceAll(" - ", " ")
		.split(" ")
		.slice(0, 3)
		.map((v: string) => v.substring(0, 1))
		.join("");
}

function hashCode(str: string) {
	let hash = 0;
	for (let i = 0; i < str.length; i++) {
		hash = str.charCodeAt(i) + ((hash << 5) - hash);
	}
	return hash;
}
